import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Bed, Bath, Maximize2, Eye, DollarSign } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

type PropertyUnit = Tables<'property_units'>;

interface UnitFormData {
  availability: 'available' | 'reserved' | 'sold';
  view_type: 'lagoon' | 'canal';
  price: string;
  show_price: boolean;
  matterport_url: string;
  kuula_url: string;
  images: string[];
  floor_plan_images: string[];
  render_images: string[];
  render_rotation_angle: number;
}

interface UnitEditPreviewProps {
  unit: PropertyUnit | null;
  formData: UnitFormData;
}

export function UnitEditPreview({ unit, formData }: UnitEditPreviewProps) {
  if (!unit) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading preview...</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'default';
      case 'reserved':
        return 'secondary';
      case 'sold':
        return 'destructive';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6 sticky top-80">
      {/* Current Gallery Images */}
      {formData.images.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Gallery Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {formData.images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className="relative aspect-square overflow-hidden rounded-lg bg-muted"
                >
                  <img
                    src={image}
                    alt={`Gallery ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                  {index === 3 && formData.images.length > 4 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="text-white font-semibold">
                        +{formData.images.length - 4} more
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unit Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle>{unit.unit_number}</CardTitle>
            <Badge variant={getStatusColor(formData.availability)}>
              {formData.availability}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Price */}
          {formData.price && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">
                  ${parseFloat(formData.price).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formData.show_price ? 'Visible to public' : 'Hidden from public'}
                </p>
              </div>
            </div>
          )}

          {/* Unit Details */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Bed className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{unit.bedrooms} Bedrooms</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bath className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{unit.bathrooms} Bathrooms</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Block {unit.block}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Maximize2 className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{unit.area_total} sq ft</p>
              </div>
            </div>
          </div>

          {/* View Type */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">
              <span className="font-medium capitalize">{formData.view_type}</span> View
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Floor Plans Preview */}
      {formData.floor_plan_images.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Floor Plans</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {formData.floor_plan_images.map((image, index) => (
                <div
                  key={index}
                  className="relative aspect-square overflow-hidden rounded-lg bg-muted"
                >
                  <img
                    src={image}
                    alt={`Floor plan ${index + 1}`}
                    className="h-full w-full object-contain"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3D Renders Preview */}
      {formData.render_images.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">3D Renders</CardTitle>
              {formData.render_rotation_angle !== 0 && (
                <Badge variant="secondary" className="text-xs">
                  {formData.render_rotation_angle}° rotation
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {formData.render_images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className="relative aspect-square overflow-hidden rounded-lg bg-muted flex items-center justify-center"
                >
                  <img
                    src={image}
                    alt={`3D render ${index + 1}`}
                    className="max-h-full max-w-full object-contain transition-transform duration-300"
                    style={{ transform: formData.render_rotation_angle ? `rotate(${formData.render_rotation_angle}deg)` : undefined }}
                  />
                  {index === 3 && formData.render_images.length > 4 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="text-white font-semibold">
                        +{formData.render_images.length - 4} more
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Virtual Tours */}
      {(formData.matterport_url || formData.kuula_url) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Virtual Tours</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {formData.matterport_url && (
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span>Matterport tour available</span>
              </div>
            )}
            {formData.kuula_url && (
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span>360° tour available</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
